import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  HelpCircle,
  Clock,
  DollarSign,
  Calculator,
  CheckCircle,
  Edit,
  Trash2,
  Calendar,
  Box,
  ChevronRight,
  PlusCircle,
  FileText,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import TimeMaterialsService from '../../services/timeMaterialsService';
import { useToast, ToastContainer } from '../common/Toast';
import ConfirmationModal from '../common/ConfirmationModal';

const getEmptyLaborForm = () => ({
  person: '',
  resourceName: '',
  resourceId: '',
  hours: '',
  hourlyRate: '',
  date: '',
  description: '',
});

const getEmptyMaterialForm = () => ({
  item: '',
  quantity: '',
  unitCost: '',
  description: '',
});

const formatCurrency = (value = 0, currency = 'USD') => {
  const amount = typeof value === 'number' ? value : Number(value) || 0;
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency,
  });
};

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const transformContract = (contract) => ({
  id: contract.id || contract.contractId,
  title: contract.title || contract.name || 'Untitled Contract',
  counterparty: contract.counterparty,
  status: (contract.status || 'draft').toLowerCase(),
  startDate: contract.startDate,
  endDate: contract.endDate,
  amount: contract.amount || 0,
  currency: contract.currency || 'USD',
  paymentTerms: contract.paymentTerms || 'NET_30',
});

const TimeMaterialsContract = () => {
  const navigate = useNavigate();
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [timeMaterials, setTimeMaterials] = useState(null);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [loadingTimeMaterials, setLoadingTimeMaterials] = useState(false);
  const [laborForm, setLaborForm] = useState(getEmptyLaborForm());
  const [materialForm, setMaterialForm] = useState(getEmptyMaterialForm());
  const [editingLaborId, setEditingLaborId] = useState(null);
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [isSavingLabor, setIsSavingLabor] = useState(false);
  const [isSavingMaterial, setIsSavingMaterial] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  const loadContracts = async () => {
    setLoadingContracts(true);
    try {
      const response = await ContractService.getContracts({
        type: 'Time & Materials',
        page: pagination.page,
        limit: pagination.pageSize,
      });
      const contractsData = response.data || [];
      const transformed = contractsData.map(transformContract);
      setContracts(transformed);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.totalItems || transformed.length,
      }));
      const preferredId = selectedContract?.id;
      const preferredContract =
        transformed.find((contract) => contract.id === preferredId) ||
        transformed[0] ||
        null;
      setSelectedContract(preferredContract);
    } catch (err) {
      console.error('Error loading time & materials contracts:', err);
      showError(
        err.response?.data?.message ||
          'Failed to load Time & Materials contracts'
      );
      setContracts([]);
      setSelectedContract(null);
      setTimeMaterials(null);
    } finally {
      setLoadingContracts(false);
    }
  };

  const loadTimeMaterials = async (contractId) => {
    if (!contractId) {
      setTimeMaterials(null);
      return;
    }
    setLoadingTimeMaterials(true);
    try {
      const response = await TimeMaterialsService.getTimeMaterials(contractId);
      if (!response.success) {
        throw new Error(
          response.message || 'Unable to load time material record'
        );
      }
      setTimeMaterials(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setTimeMaterials(null);
        return;
      }
      console.error('Error fetching time materials:', err);
      showError(
        err.response?.data?.message || 'Failed to fetch time & materials data'
      );
      setTimeMaterials(null);
    } finally {
      setLoadingTimeMaterials(false);
    }
  };

  useEffect(() => {
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedContract) {
      loadTimeMaterials(selectedContract.id);
    } else {
      setTimeMaterials(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract]);

  const handleRefresh = () => {
    loadContracts();
    success('Contracts refreshed');
  };

  const handleLaborInput = (field, value) => {
    setLaborForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMaterialInput = (field, value) => {
    setMaterialForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLaborSubmit = async (event) => {
    event.preventDefault();
    if (!selectedContract) {
      showError('Select a contract first');
      return;
    }
    const person = laborForm.person.trim();
    if (!person) {
      showError('Person name is required');
      return;
    }
    const hours = Number(laborForm.hours);
    const hourlyRate = Number(laborForm.hourlyRate);
    if (Number.isNaN(hours) || hours < 0) {
      showError('Hours must be a non-negative number');
      return;
    }
    if (Number.isNaN(hourlyRate) || hourlyRate < 0) {
      showError('Hourly rate must be a non-negative number');
      return;
    }
    if (!laborForm.date) {
      showError('Date is required');
      return;
    }

    const entryPayload = {
      person,
      hours: Number(hours.toFixed(2)),
      hourlyRate: Number(hourlyRate.toFixed(2)),
      date: laborForm.date,
      description: laborForm.description.trim(),
    };
    if (laborForm.resourceId) {
      entryPayload.resourceId = laborForm.resourceId.trim();
    }
    if (laborForm.resourceName) {
      entryPayload.resourceName = laborForm.resourceName.trim();
    }

    try {
      setIsSavingLabor(true);
      let result;
      if (!timeMaterials) {
        const payload = {
          laborEntries: [{ ...entryPayload }],
          hourlyRates: [],
        };
        const response = await TimeMaterialsService.createTimeMaterials(
          selectedContract.id,
          payload
        );
        if (!response.success) {
          throw new Error(
            response.message || 'Unable to create time materials record'
          );
        }
        result = response;
      } else {
        const updatedEntries = editingLaborId
          ? timeMaterials.laborEntries.map((entry) =>
              entry._id === editingLaborId
                ? { ...entryPayload, _id: entry._id }
                : entry
            )
          : [...timeMaterials.laborEntries, entryPayload];
        const response = await TimeMaterialsService.updateTimeMaterials(
          selectedContract.id,
          timeMaterials._id,
          {
            laborEntries: updatedEntries,
          }
        );
        if (!response.success) {
          throw new Error(response.message || 'Unable to update labor entries');
        }
        result = response;
      }

      setTimeMaterials(result.data);
      setLaborForm(getEmptyLaborForm());
      setEditingLaborId(null);
      success(editingLaborId ? 'Labor entry updated' : 'Labor entry added');
    } catch (err) {
      console.error('Labor entry error:', err);
      showError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save labor entry'
      );
    } finally {
      setIsSavingLabor(false);
    }
  };

  const handleMaterialSubmit = async (event) => {
    event.preventDefault();
    if (!selectedContract) {
      showError('Select a contract first');
      return;
    }
    const item = materialForm.item.trim();
    if (!item) {
      showError('Material item name is required');
      return;
    }
    const quantity = Number(materialForm.quantity);
    const unitCost = Number(materialForm.unitCost);
    if (Number.isNaN(quantity) || quantity < 0) {
      showError('Quantity must be a non-negative number');
      return;
    }
    if (Number.isNaN(unitCost) || unitCost < 0) {
      showError('Unit cost must be a non-negative number');
      return;
    }

    const entryPayload = {
      item,
      quantity: Number(quantity.toFixed(2)),
      unitCost: Number(unitCost.toFixed(2)),
      description: materialForm.description.trim(),
    };

    try {
      setIsSavingMaterial(true);
      let result;
      if (!timeMaterials) {
        const response = await TimeMaterialsService.createTimeMaterials(
          selectedContract.id,
          {
            materialEntries: [{ ...entryPayload }],
            hourlyRates: [],
          }
        );
        if (!response.success) {
          throw new Error(
            response.message || 'Unable to create time materials record'
          );
        }
        result = response;
      } else {
        const updatedEntries = editingMaterialId
          ? timeMaterials.materialEntries.map((entry) =>
              entry._id === editingMaterialId
                ? { ...entryPayload, _id: entry._id }
                : entry
            )
          : [...(timeMaterials.materialEntries || []), entryPayload];

        const response = await TimeMaterialsService.updateTimeMaterials(
          selectedContract.id,
          timeMaterials._id,
          {
            materialEntries: updatedEntries,
          }
        );
        if (!response.success) {
          throw new Error(
            response.message || 'Unable to save material entries'
          );
        }
        result = response;
      }

      setTimeMaterials(result.data);
      setMaterialForm(getEmptyMaterialForm());
      setEditingMaterialId(null);
      success(
        editingMaterialId ? 'Material entry updated' : 'Material entry added'
      );
    } catch (err) {
      console.error('Material entry error:', err);
      showError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save material entry'
      );
    } finally {
      setIsSavingMaterial(false);
    }
  };

  const handleEditLabor = (entry) => {
    setEditingLaborId(entry._id);
    setLaborForm({
      person: entry.person,
      resourceName: entry.resourceName || '',
      resourceId: entry.resourceId || '',
      hours: entry.hours,
      hourlyRate: entry.hourlyRate,
      date: formatDateForInput(entry.date),
      description: entry.description || '',
    });
  };

  const handleCancelLaborEdit = () => {
    setEditingLaborId(null);
    setLaborForm(getEmptyLaborForm());
  };

  const handleEditMaterial = (entry) => {
    setEditingMaterialId(entry._id);
    setMaterialForm({
      item: entry.item,
      quantity: entry.quantity,
      unitCost: entry.unitCost,
      description: entry.description || '',
    });
  };

  const handleCancelMaterialEdit = () => {
    setEditingMaterialId(null);
    setMaterialForm(getEmptyMaterialForm());
  };

  const handleDeleteLabor = async (entryId) => {
    if (!timeMaterials || !entryId) return;
    if (!window.confirm('Remove this labor entry from the contract?')) return;
    try {
      setIsSavingLabor(true);
      const updatedEntries = timeMaterials.laborEntries.filter(
        (entry) => entry._id !== entryId
      );
      const response = await TimeMaterialsService.updateTimeMaterials(
        selectedContract.id,
        timeMaterials._id,
        {
          laborEntries: updatedEntries,
        }
      );
      if (!response.success) {
        throw new Error(response.message || 'Unable to delete labor entry');
      }
      setTimeMaterials(response.data);
      success('Labor entry removed');
    } catch (err) {
      console.error('Delete labor entry error:', err);
      showError(
        err.response?.data?.message ||
          err.message ||
          'Failed to delete labor entry'
      );
    } finally {
      setIsSavingLabor(false);
    }
  };

  const handleDeleteMaterial = async (entryId) => {
    if (!timeMaterials || !entryId) return;
    if (!window.confirm('Remove this material entry from the contract?'))
      return;
    try {
      setIsSavingMaterial(true);
      const updatedEntries = timeMaterials.materialEntries.filter(
        (entry) => entry._id !== entryId
      );
      const response = await TimeMaterialsService.updateTimeMaterials(
        selectedContract.id,
        timeMaterials._id,
        {
          materialEntries: updatedEntries,
        }
      );
      if (!response.success) {
        throw new Error(response.message || 'Unable to delete material entry');
      }
      setTimeMaterials(response.data);
      success('Material entry removed');
    } catch (err) {
      console.error('Delete material entry error:', err);
      showError(
        err.response?.data?.message ||
          err.message ||
          'Failed to delete material entry'
      );
    } finally {
      setIsSavingMaterial(false);
    }
  };

  const getEntryStatusLabel = () => {
    const status = timeMaterials?.status || 'draft';
    const colorClass =
      status === 'active'
        ? 'text-emerald-600 bg-emerald-50'
        : status === 'closed'
        ? 'text-red-600 bg-red-50'
        : 'text-slate-600 bg-slate-50';
    return (
      <div
        className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${colorClass}`}
      >
        <CheckCircle size={12} />
        <span>{status}</span>
      </div>
    );
  };

  const handleDeleteContract = async () => {
    if (!contractToDelete) return;
    
    setIsDeleting(true);
    try {
      const contractId = contractToDelete.contractId || contractToDelete.id;
      const response = await ContractService.deleteContract(contractId);
      
      if (response.success) {
        success('Contract deleted successfully');
        setShowDeleteModal(false);
        setContractToDelete(null);
        if (selectedContract?.id === contractToDelete.id) {
          setSelectedContract(null);
          setTimeMaterials(null);
        }
        loadContracts();
      } else {
        showError(response.message || 'Failed to delete contract');
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      showError(error.response?.data?.message || error.message || 'Failed to delete contract');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loadingContracts && contracts.length === 0) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader
          title="Time & Materials Contracts"
          description="Trustworthy labor and material tracking backed by Time & Materials logic"
        />
        <div className="page-container">
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '320px' }}
          >
            <div
              className="spinner-border text-primary mb-3"
              role="status"
            ></div>
            <p className="text-muted">Loading contracts...</p>
          </div>
        </div>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </>
    );
  }

  return (
    <div>
      <PageBreadcrumb />
      <PageHeader
        title="Time & Materials Contracts"
        description="Track labor hours, materials, and costs in one place."
        actions={[
          {
            label: '',
            icon: <RefreshCw size={18} />,
            onClick: handleRefresh,
            variant: 'outline',
            tooltip: 'Refresh data',
            disabled: loadingContracts,
          },
          {
            label: '',
            icon: <HelpCircle size={18} />,
            onClick: () => openPageHelp('time-materials-contracts'),
            variant: 'outline',
            tooltip: 'Help - Time & Materials',
          },
          {
            label: 'New T&M Contract',
            icon: <Plus size={18} />,
            onClick: () => navigate('/contracts/time-materials/create'),
            variant: 'primary',
            tooltip: 'Create new contract',
          },
        ]}
      />

      <div className="page-container">
        {contracts.length === 0 ? (
          <div className="card">
            <div className="card-body text-center">
              <h5>No Time & Materials contracts available</h5>
              <p className="text-muted">
                Create a contract to start logging labor and material costs.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/contracts/time-materials/create')}
              >
                <Plus size={16} className="me-1" />
                Create T&M Contract
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-xs font-black uppercase tracking-widest px-1">
                Selected Contract
              </h2>
              <div className="flex overflow-x-auto pb-4 gap-4 no-scrollbar">
                {contracts.map((contract) => (
                  <div>
                    <button
                      key={contract.id}
                      onClick={() => setSelectedContract(contract)}
                      className={`flex-shrink-0 w-72 text-left p-5 rounded-2xl border transition-all duration-200 group
                      ${
                        contract.id === selectedContract?.id
                          ? 'border-[var(--primary-color)] bg-white ring-4 ring-[var(--accent-color)] shadow-sm'
                          : 'bg-[var(--white)] border-[var(--border-color)] hover:shadow-lg'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h4
                          className={`font-bold truncate ${
                            contract.id === selectedContract?.id
                              ? 'text-[var(--text-heading)]'
                              : 'text-[var(--text-primary)]'
                          }`}
                        >
                          {contract.title}
                        </h4>

                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-tighter
                          ${
                            contract.id === selectedContract?.id
                              ? 'bg-[var(--primary-color)] text-[var(--white)]'
                              : 'bg-[var(--secondary-color)] text-[var(--text-muted)]'
                          }`}
                        >
                          {contract.status}
                        </span>
                      </div>

                      <p
                        className={`text-sm mb-6 line-clamp-1 font-medium ${
                          contract.id === selectedContract?.id
                            ? 'text-[var(--text-secondary)]'
                            : 'text-[var(--text-muted)]'
                        }`}
                      >
                        {contract.counterparty}
                      </p>

                      <div
                        className={`flex items-center justify-between pt-4 border-t  ${
                          contract.id === selectedContract?.id
                            ? 'border-[var(--divider-color)]'
                            : 'border-[var(--border-color)]'
                        }`}
                      >
                        <div
                          className={`flex items-center gap-1.5 text-xs font-bold ${
                            contract.id === selectedContract?.id
                              ? 'text-[var(--text-primary)]'
                              : 'text-[var(--text-muted)]'
                          }`}
                        >
                          <Calendar size={14} />
                          <span>
                            {contract.startDate
                              ? new Date(
                                  contract.startDate
                                ).toLocaleDateString()
                              : 'N/A'}
                          </span>
                        </div>

                        <ChevronRight
                          size={16}
                          className={
                            contract.id === selectedContract?.id
                              ? 'text-[var(--primary-color)]'
                              : 'text-[var(--text-muted)] group-hover:text-[var(--primary-color)]'
                          }
                        />
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  label: 'Labor Cost',
                  value: timeMaterials?.totalLaborCost,
                  icon: DollarSign,
                  count: timeMaterials?.laborEntries?.length || 0,
                  color: 'blue',
                },
                {
                  label: 'Material Cost',
                  value: timeMaterials?.totalMaterialCost,
                  icon: DollarSign,
                  count: timeMaterials?.materialEntries?.length || 0,
                  color: 'emerald',
                },
                {
                  label: 'Grand Total',
                  value: timeMaterials?.grandTotal,
                  icon: Calculator,
                  ceiling: timeMaterials?.costTracking?.costCeiling,
                  color: 'slate',
                },
              ].map((metric, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                      {metric.label}
                    </span>
                    <metric.icon
                      size={20}
                      className={`text-${metric.color}-500`}
                    />
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-3xl font-black text-slate-900 tracking-tight">
                      {formatCurrency(
                        metric.value || 0,
                        selectedContract?.currency
                      )}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-slate-500 flex items-center gap-1.5">
                    {metric.ceiling ? (
                      <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md text-xs font-bold">
                        {Math.round(
                          ((metric.value || 0) / (metric.ceiling || 1)) * 100
                        )}
                        % of ceiling
                      </span>
                    ) : (
                      <span className="text-slate-400 font-medium">
                        {metric.count !== undefined
                          ? `${metric.count} Entries recorded`
                          : 'No ceiling set'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Time Tracking */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[var(--primary-color)] text-white rounded-lg">
                    <Clock size={18} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 tracking-tight">
                    Time Tracking
                  </h3>
                </div>

                <div className="space-y-2 text-sm font-semibold text-slate-700">
                  <div className="flex justify-between">
                    <span>Enabled</span>
                    <span>
                      {timeMaterials?.timeTracking?.enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Required</span>
                    <span>
                      {timeMaterials?.timeTracking?.required ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Approval Required</span>
                    <span>
                      {timeMaterials?.timeTracking?.approvalRequired
                        ? 'Yes'
                        : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Max Hours / Day</span>
                    <span>
                      {timeMaterials?.timeTracking?.maxHoursPerDay ?? 8}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cost Tracking */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[var(--primary-color)] text-white rounded-lg">
                    <DollarSign size={18} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 tracking-tight">
                    Cost Tracking
                  </h3>
                </div>

                <div className="space-y-2 text-sm font-semibold text-slate-700">
                  <div className="flex justify-between">
                    <span>Cost Ceiling</span>
                    <span>
                      {formatCurrency(
                        timeMaterials?.costTracking?.costCeiling || 0,
                        selectedContract?.currency
                      )}
                    </span>
                  </div>

                  <div>
                    <span className="block mb-1">Categories</span>
                    <span className="text-slate-500 font-medium">
                      {(timeMaterials?.costTracking?.categories || []).join(
                        ', '
                      ) || 'Not configured'}
                    </span>
                  </div>

                  <div>
                    <span className="block mb-1">Budget Limits</span>
                    <span className="text-slate-500 font-medium">
                      {(timeMaterials?.costTracking?.budgetLimits || []).join(
                        ', '
                      ) || 'None'}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Labor Tracking Section */}
              <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[var(--primary-color)] text-white rounded-lg">
                      <Clock size={20} />
                    </div>
                    <span className="font-extrabold text-slate-900 tracking-tight">
                      Labor Tracking
                    </span>
                  </div>
                  {getEntryStatusLabel()}
                </div>

                <div className="p-6">
                  <form
                    onSubmit={handleLaborSubmit}
                    className="space-y-4 mb-10 bg-slate-50 p-6 rounded-xl border border-slate-100"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Person Name
                        </label>
                        <input
                          type="text"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 focus:border-blue-500 outline-none transition-all"
                          value={laborForm.person}
                          onChange={(e) =>
                            handleLaborInput('person', e.target.value)
                          }
                          placeholder="e.g. John Doe"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Date
                        </label>
                        <input
                          type="date"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 focus:border-blue-500 outline-none transition-all"
                          value={laborForm.date}
                          onChange={(e) =>
                            handleLaborInput('date', e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider ">
                          Resource Name
                        </label>
                        <input
                          type="text"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 focus:border-blue-500 outline-none transition-all"
                          value={laborForm.resourceName}
                          onChange={(e) =>
                            handleLaborInput('resourceName', e.target.value)
                          }
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Resource ID
                        </label>
                        <input
                          type="text"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 focus:border-blue-500 outline-none transition-all"
                          value={laborForm.resourceId}
                          onChange={(e) =>
                            handleLaborInput('resourceId', e.target.value)
                          }
                          placeholder="Optional internal ID"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Hours
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 outline-none transition-all"
                          value={laborForm.hours}
                          onChange={(e) =>
                            handleLaborInput('hours', e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Hourly Rate
                        </label>
                        <div className="relative">
                          <DollarSign
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="number"
                            step="0.01"
                            className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 outline-none transition-all"
                            value={laborForm.hourlyRate}
                            onChange={(e) =>
                              handleLaborInput('hourlyRate', e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                        Description
                      </label>
                      <textarea
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 outline-none transition-all resize-none"
                        rows={2}
                        value={laborForm.description}
                        onChange={(e) =>
                          handleLaborInput('description', e.target.value)
                        }
                        placeholder="Work performed..."
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        type="submit"
                        disabled={isSavingLabor}
                        className="flex-1 bg-[var(--primary-color)] hover:bg-white hover:text-[var(--primary-color)] hover:border hover:border-[color:var(--primary-color)] text-white py-2 rounded-lg font-bold text-sm shadow-md shadow-blue-100 transition-all disabled:opacity-50"
                      >
                        {editingLaborId ? 'Update Entry' : 'Add Labor Entry'}
                      </button>
                      {editingLaborId && (
                        <button
                          type="button"
                          onClick={handleCancelLaborEdit}
                          className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>

                  <div className="overflow-x-auto -mx-6">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 border-y border-slate-100">
                          <th className="px-6 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">
                            Person
                          </th>
                          <th className="px-3 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">
                            Hours
                          </th>
                          <th className="px-3 py-3 text-xs font-black text-slate-500 uppercase tracking-wider text-right">
                            Rate
                          </th>
                          <th className="px-3 py-3 text-xs font-black text-slate-500 uppercase tracking-wider text-right">
                            Cost
                          </th>
                          <th className="px-6 py-3 text-xs font-black text-slate-500 uppercase tracking-wider text-right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {timeMaterials?.laborEntries?.length > 0 ? (
                          timeMaterials.laborEntries.map((entry) => (
                            <tr
                              key={entry._id}
                              className="hover:bg-slate-50/50 transition-colors group"
                            >
                              <td className="px-6 py-4">
                                <div className="font-bold text-slate-900 leading-tight">
                                  {entry.person}
                                </div>
                                <div className="text-xs text-slate-400 font-medium">
                                  {new Date(entry.date).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-3 py-4 text-sm font-black text-slate-700">
                                {entry.hours}h
                              </td>
                              <td className="px-3 py-4 text-sm font-black text-slate-900 text-right">
                                {formatCurrency(
                                  entry.hourlyRate,
                                  selectedContract?.currency
                                )}
                              </td>
                              <td className="px-3 py-4 text-sm font-black text-slate-900 text-right">
                                {formatCurrency(
                                  entry.hours * entry.hourlyRate,
                                  selectedContract?.currency
                                )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-1">
                                  <button
                                    onClick={() => handleEditLabor(entry)}
                                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLabor(entry._id)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-6 py-12 text-center text-slate-400 font-medium"
                            >
                              No labor entries recorded
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {/* Material Tracking Section */}
              <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[var(--primary-color)] text-white rounded-lg">
                      <Box size={20} />
                    </div>
                    <span className="font-extrabold text-slate-900 tracking-tight">
                      Material Tracking
                    </span>
                  </div>
                  {getEntryStatusLabel()}
                </div>

                <div className="p-6">
                  <form
                    onSubmit={handleMaterialSubmit}
                    className="space-y-4 mb-10 bg-slate-50 p-6 rounded-xl border border-slate-100"
                  >
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                        Item Name
                      </label>
                      <input
                        type="text"
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 focus:border-emerald-500 outline-none transition-all"
                        value={materialForm.item}
                        onChange={(e) =>
                          handleMaterialInput('item', e.target.value)
                        }
                        placeholder="Material or resource name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Quantity
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 outline-none transition-all"
                          value={materialForm.quantity}
                          onChange={(e) =>
                            handleMaterialInput('quantity', e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Unit Cost
                        </label>
                        <div className="relative">
                          <DollarSign
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="number"
                            step="0.01"
                            className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 outline-none transition-all"
                            value={materialForm.unitCost}
                            onChange={(e) =>
                              handleMaterialInput('unitCost', e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-500 uppercase tracking-wider">
                        Description
                      </label>
                      <textarea
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-var(--primary-color)-500 outline-none transition-all resize-none"
                        rows={2}
                        value={materialForm.description}
                        onChange={(e) =>
                          handleMaterialInput('description', e.target.value)
                        }
                        placeholder="Purpose or location..."
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        type="submit"
                        disabled={isSavingMaterial}
                        className="flex-1 bg-[var(--primary-color)] hover:bg-white hover:text-[var(--primary-color)] hover:border hover:border-[color:var(--primary-color)] text-white py-2 rounded-lg font-bold text-sm shadow-md shadow-blue-100 transition-all disabled:opacity-50"
                      >
                        {editingMaterialId
                          ? 'Update Entry'
                          : 'Add Material Entry'}
                      </button>
                      {editingMaterialId && (
                        <button
                          type="button"
                          onClick={handleCancelMaterialEdit}
                          className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>

                  <div className="overflow-x-auto -mx-6">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 border-y border-slate-100">
                          <th className="px-6 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">
                            Item
                          </th>
                          <th className="px-3 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">
                            Qty
                          </th>
                          <th className="px-3 py-3 text-xs font-black text-slate-500 uppercase tracking-wider text-right">
                            Total
                          </th>
                          <th className="px-6 py-3 text-xs font-black text-slate-500 uppercase tracking-wider text-right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {timeMaterials?.materialEntries?.length > 0 ? (
                          timeMaterials.materialEntries.map((entry) => (
                            <tr
                              key={entry._id}
                              className="hover:bg-slate-50/50 transition-colors group"
                            >
                              <td className="px-6 py-4">
                                <div className="font-bold text-slate-900 leading-tight">
                                  {entry.item}
                                </div>
                                <div className="text-xs text-slate-400 font-medium line-clamp-1">
                                  {entry.description || 'No description'}
                                </div>
                              </td>
                              <td className="px-3 py-4 text-sm font-black text-slate-700">
                                {entry.quantity}
                              </td>
                              <td className="px-3 py-4 text-sm font-black text-slate-900 text-right">
                                {formatCurrency(
                                  entry.quantity * entry.unitCost,
                                  selectedContract?.currency
                                )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-1">
                                  <button
                                    onClick={() => handleEditMaterial(entry)}
                                    className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-md"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteMaterial(entry._id)
                                    }
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-6 py-12 text-center text-slate-400 font-medium"
                            >
                              No material entries recorded
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setContractToDelete(null);
        }}
        onConfirm={handleDeleteContract}
        title="Delete Time & Materials Contract"
        message={`Are you sure you want to delete contract "${contractToDelete?.title || contractToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TimeMaterialsContract;